import Vue from 'vue'
import VueRouter from 'vue-router'
import { spaMetaUpdateAndTrack } from '@/utils/head'

// code splitting
/* ANALYTICS-TRACKERS ROUTES */
// NOTE: apexcharts and vue-apexcharts are part of the vendor chunk for analytics
const DashboardStatsPage = () =>
  import(
    /* webpackChunkName: "analytics-trackers" */ './components/analytics/dashboard-stats-page.vue'
  )
const DashboardSchedulablePage = () =>
  import(
    /* webpackChunkName: "analytics-trackers" */ './components/analytics/dashboard-schedulable-page.vue'
  )
const DashboardCommunityPage = () =>
  import(
    /* webpackChunkName: "analytics-trackers" */ './components/analytics/dashboard-community-page.vue'
  )
const WorkoutDetailDashboardPage = () =>
  import(
    /* webpackChunkName: "analytics-trackers" */ './components/analytics/details/workout-dashboard-page.vue'
  )
const SchedulableCompletesPage = () =>
  import(
    /* webpackChunkName: "analytics-trackers" */ './components/analytics/details/schedulable-completes-page.vue'
  )
const DashboardNotesPage = () =>
  import(/* webpackChunkName: "analytics-trackers" */ './components/notes/dashboard-notes-page.vue')
const ArticleDetailDashboardPage = () =>
  import(
    /* webpackChunkName: "analytics-trackers" */ './components/analytics/details/article-dashboard-page.vue'
  )
const WellnessVideoDetailDashboardPage = () =>
  import(
    /* webpackChunkName: "analytics-trackers" */ './components/analytics/details/wellness-video-dashboard-page.vue'
  )
const OrganizationDashboardPage = () =>
  import(
    /* webpackChunkName: "analytics-trackers" */ './components/organizations/dashboard-page.vue'
  )
const OrganizationUsersPage = () =>
  import(
    /* webpackChunkName: "analytics-trackers" */ './components/organizations/dashboard-users-page.vue'
  )

/* ACHIEVEMENTS ROUTES */
const AchievementsPage = () =>
  import(/* webpackChunkName: "user" */ './components/achievements/achievements-page.vue')

/* NOTIFICATION ROUTES */
const NotificationsPage = () =>
  import(/* webpackChunkName: "user" */ './components/notifications/notifications-page.vue')

const EnergyPage = () =>
  import(/* webpackChunkName: "user" */ './components/users/settings/energy/energy-page.vue')

/* SEARCH ROUTES */
const VideosPage = () =>
  import(/* webpackChunkName: "search" */ './components/videos/videos-page.vue')
const VideosBrowsePage = () =>
  import(/* webpackChunkName: "search" */ './components/videos/videos-browse-page.vue')
const WellnessVideosPage = () =>
  import(/* webpackChunkName: "search" */ './components/wellness-videos/wellness-videos-page.vue')
const SimilarVideosPage = () =>
  import(/* webpackChunkName: "search" */ './components/videos/similar-videos-page.vue')
const PlansPage = () => import(/* webpackChunkName: "search" */ './components/plans/plans-page.vue')
const PlansBrowsePage = () =>
  import(/* webpackChunkName: "search" */ './components/plans/plans-browse-page.vue')

const MealPlansPage = () =>
  import(/* webpackChunkName: "search" */ './components/plans/meal-plans-page.vue')
const PilotProgramsPage = () =>
  import(/* webpackChunkName: "search" */ './components/plans/pilot-programs-page.vue')
const ArticlesPage = () =>
  import(/* webpackChunkName: "search" */ './components/articles/articles-page.vue')
const RecipesPage = () =>
  import(/* webpackChunkName: "search" */ './components/articles/recipes-page.vue')

/* ROUTINES ROUTES */
// using search chunk as routine building happens to use video / favorites search
const RoutinesPage = () =>
  import(/* webpackChunkName: "search" */ './components/routines/routines-page.vue')

/* CUSTOM WORKOUTS ROUTES */
const CustomWorkoutsPage = () =>
  import(/* webpackChunkName: "search" */ './components/custom-workouts/custom-workouts-page.vue')

// attach router to vue instance
Vue.use(VueRouter)

const createSpaRoutes = () => {
  const router = new VueRouter({
    mode: 'history',
    routes: [
      /* workout videos */
      {
        name: 'Videos',
        meta: {
          title: 'Workout Videos',
          gaTrack: true,
          focusTitle: false, // managed on videos-page.vue
          scrollTo: null,
        },
        path: '/videos',
        component: VideosPage,
      },
      {
        name: 'VideoBrowse',
        meta: {
          title: 'Browse Workout Videos',
          gaTrack: true,
        },
        path: '/videos/browse',
        component: VideosBrowsePage,
      },
      {
        name: 'SimilarVideos',
        meta: {
          title: 'Similar Workout Videos',
          gaTrack: true,
          focusTitle: false, // managed on similar-videos-page.vue
          scrollTo: null,
        },
        path: '/videos/similar',
        component: SimilarVideosPage,
      },
      {
        name: 'FavoriteVideos',
        meta: {
          title: 'Favorite Videos',
          gaTrack: true,
          focusTitle: false, // managed on videos-page.vue
          scrollTo: null,
        },
        path: '/my/favorite/videos',
        component: VideosPage,
      },

      /* wellness videos */
      {
        name: 'WellnessVideos',
        meta: {
          title: 'Wellness Videos',
          gaTrack: true,
          focusTitle: false, // managed on wellness-videos-page.vue
          scrollTo: null,
        },
        path: '/wellness-videos',
        component: WellnessVideosPage,
      },
      {
        name: 'FavoriteWellnessVideos',
        meta: {
          title: 'Favorite Wellness Videos',
          gaTrack: true,
          focusTitle: false, // managed on wellness-videos-page.vue
          scrollTo: null,
        },
        path: '/my/favorite/wellness-videos',
        component: WellnessVideosPage,
      },

      {
        name: 'Plans',
        meta: {
          title: 'Workout Programs',
          gaTrack: true,
          focusTitle: false, // managed on plans-page.vue
          scrollTo: null,
        },
        path: '/plans',
        component: PlansPage,
      },
      {
        name: 'MyPrograms',
        meta: {
          title: 'My Workout Programs',
          gaTrack: true,
          focusTitle: false, // managed on plans-page.vue
          scrollTo: null,
        },
        path: '/my/programs',
        component: PlansPage,
      },
      {
        name: 'WorkoutPlanBrowse',
        meta: {
          title: 'Browse Workout Programs',
          gaTrack: true,
        },
        path: '/plans/browse',
        component: PlansBrowsePage,
      },
      {
        name: 'MyProgramsBrowse',
        meta: {
          title: 'Browse My Workout Programs',
          gaTrack: true,
        },
        path: '/my/programs/browse',
        component: PlansBrowsePage,
      },
      {
        name: 'MealPlans',
        meta: {
          title: 'Meal Plans',
          gaTrack: true,
          focusTitle: false, // managed on meal-plans-page.vue
          scrollTo: null,
        },
        path: '/meal-plans',
        component: MealPlansPage,
      },
      {
        name: 'MyMealPlans',
        meta: {
          title: 'My Meal Plans',
          gaTrack: true,
          focusTitle: false, // managed on meal-plans-page.vue
          scrollTo: null,
        },
        path: '/my/meal-plans',
        component: MealPlansPage,
      },

      /* pilot programs */
      {
        name: 'PilotPrograms',
        meta: {
          title: 'Pilot Programs',
          gaTrack: true,
          focusTitle: false, // managed on meal-plans-page.vue
          scrollTo: null,
        },
        path: '/pilot-programs',
        component: PilotProgramsPage,
      },
      {
        name: 'MyPilotPrograms',
        meta: {
          title: 'My Pilot Programs',
          gaTrack: true,
          focusTitle: false, // managed on meal-plans-page.vue
          scrollTo: null,
        },
        path: '/my/pilot-programs',
        component: PilotProgramsPage,
      },

      /* achievements */
      {
        name: 'Achievements',
        meta: {
          title: 'Achievements',
          gaTrack: true,
        },
        path: '/my/achievements/:type',
        component: AchievementsPage,
      },

      /* notifications */
      {
        name: 'Notifications',
        meta: {
          title: 'Notifications',
          gaTrack: true,
        },
        path: '/my/notifications',
        component: NotificationsPage,
      },

      /* energy settings, multiple routes handled by single SPA */
      {
        name: 'EnergySettings',
        meta: {
          title: 'Energy Settings',
          gaTrack: true,
        },
        path: '/my/settings/energy/:section(dietary|calorie)?/:energyMethod(range|goal|calculator)?',
        component: EnergyPage,
      },

      /* article-search (only recipes currently, see ArticlesController->categoryIndex) */
      {
        name: 'Articles',
        meta: {
          title: 'Articles',
          gaTrack: true,
          focusTitle: false, // managed on articles-page.vue
          scrollTo: null,
        },
        path: '/articles',
        component: ArticlesPage,
      },
      {
        name: 'Recipes',
        meta: {
          title: 'Recipes',
          gaTrack: true,
          focusTitle: false, // managed on recipes-page.vue
          scrollTo: null,
        },
        path: '/healthy-living/healthy-recipes',
        component: RecipesPage,
      },
      {
        name: 'FavoriteArticles',
        meta: {
          title: 'Favorite Articles',
          gaTrack: true,
          focusTitle: false, // managed on recipes-page.vue
          scrollTo: null,
        },
        path: '/my/favorite/articles',
        component: ArticlesPage,
      },
      {
        name: 'FavoriteRecipes',
        meta: {
          title: 'Favorite Recipes',
          gaTrack: true,
          focusTitle: false, // managed on recipes-page.vue
          scrollTo: null,
        },
        path: '/my/favorite/recipes',
        component: RecipesPage,
      },

      /* custom-workouts */
      {
        name: 'CustomWorkouts',
        meta: {
          title: 'My Custom Workouts',
          gaTrack: true,
        },
        path: '/my/custom-workouts',
        component: CustomWorkoutsPage,
      },

      /* routines */
      {
        name: 'Routines',
        meta: {
          title: 'My Routines',
          gaTrack: true,
          focusTitle: false, // managed on routines-page.vue
          scrollTo: null,
        },
        path: '/my/routines',
        component: RoutinesPage,
      },

      /* analytics SPA routes, title should mirror what is in MyDashboardController->analyticsSPA */
      {
        name: 'DashboardStats',
        meta: {
          title: 'Dashboard Stats',
          gaTrack: true,
        },
        path: '/my/dashboard/stats',
        component: DashboardStatsPage,
      },
      {
        name: 'DashboardWorkouts',
        meta: {
          title: 'Dashboard Completed History',
          gaTrack: true,
        },
        path: '/my/dashboard/:schedulableType(workouts|custom-workouts|wellness-videos|articles|recipes)/:grouping(top)?',
        component: DashboardSchedulablePage,
      },
      {
        name: 'DashboardCommunity',
        meta: {
          title: 'Dashboard Community',
          gaTrack: true,
        },
        path: '/my/dashboard/:postType(comments|discussions)',
        component: DashboardCommunityPage,
      },

      /*
        analytics + tracker SPA routes
        - workout: title should mirror what is in VideoController->show
        - custom_workout: title should mirror what is in CustomWorkouts->show
        - article: title should mirror what is in Article->show
       */
      // video workout dashboards
      {
        name: 'WorkoutDetailDashboard',
        meta: {
          title: 'Workout Dashboard',
          gaTrack: true,
          scrollTo: '#spa-top',
          scrollDelay: 25,
        },
        path: '/videos/:slug/dashboard',
        component: WorkoutDetailDashboardPage,
      },
      {
        name: 'WorkoutDetailCompletes',
        meta: {
          title: 'Workout Completes',
          gaTrack: true,
          scrollTo: '#spa-top',
          scrollDelay: 25,
        },
        path: '/videos/:slug/dashboard/completes',
        component: SchedulableCompletesPage,
      },
      {
        name: 'WorkoutDetailNotes',
        meta: {
          title: 'Workout Notes',
          gaTrack: true,
          scrollTo: '#spa-top',
          scrollDelay: 25,
        },
        path: '/videos/:slug/dashboard/notes',
        component: DashboardNotesPage,
      },
      // custom workouts dashboards
      {
        name: 'CustomWorkoutDetailDashboard',
        meta: {
          title: 'Workout Dashboard',
          gaTrack: true,
          scrollTo: '#spa-top',
          scrollDelay: 25,
        },
        path: '/my/custom-workouts/:id/:slug/dashboard',
        component: WorkoutDetailDashboardPage,
      },
      {
        name: 'CustomWorkoutDetailCompletes',
        meta: {
          title: 'Workout Completes',
          gaTrack: true,
          scrollTo: '#spa-top',
          scrollDelay: 25,
        },
        path: '/my/custom-workouts/:id/:slug/dashboard/completes',
        component: SchedulableCompletesPage,
      },
      {
        name: 'CustomWorkoutDetailNotes',
        meta: {
          title: 'Workout Notes',
          gaTrack: true,
          scrollTo: '#spa-top',
          scrollDelay: 25,
        },
        path: '/my/custom-workouts/:id/:slug/dashboard/notes',
        component: DashboardNotesPage,
      },
      // articles dashboards
      {
        name: 'ArticleDetailDashboard',
        meta: {
          title: 'Article Dashboard',
          gaTrack: true,
          scrollTo: '#spa-top',
          scrollDelay: 25,
        },
        path: '/articles/:slug/dashboard',
        component: ArticleDetailDashboardPage,
      },
      {
        name: 'ArticleDetailNotes',
        meta: {
          title: 'Article Notes',
          gaTrack: true,
          scrollTo: '#spa-top',
          scrollDelay: 25,
        },
        path: '/articles/:slug/dashboard/notes',
        component: DashboardNotesPage,
      },
      {
        name: 'ArticleDetailCompletes',
        meta: {
          title: 'Article Completes',
          gaTrack: true,
          scrollTo: '#spa-top',
          scrollDelay: 25,
        },
        path: '/articles/:slug/dashboard/completes',
        component: SchedulableCompletesPage,
      },

      // wellness video dashboards
      {
        name: 'WellnessVideoDetailDashboard',
        meta: {
          title: 'Wellness Dashboard',
          gaTrack: true,
          scrollTo: '#spa-top',
          scrollDelay: 25,
        },
        path: '/wellness-videos/:slug/dashboard',
        component: WellnessVideoDetailDashboardPage,
      },
      {
        name: 'WellnessVideoDetailNotes',
        meta: {
          title: 'Wellness Notes',
          gaTrack: true,
          scrollTo: '#spa-top',
          scrollDelay: 25,
        },
        path: '/wellness-videos/:slug/dashboard/notes',
        component: DashboardNotesPage,
      },
      {
        name: 'WellnessVideoDetailCompletes',
        meta: {
          title: 'Wellness Completes',
          gaTrack: true,
          scrollTo: '#spa-top',
          scrollDelay: 25,
        },
        path: '/wellness-videos/:slug/dashboard/completes',
        component: SchedulableCompletesPage,
      },

      // organization dashboards
      {
        name: 'OrganizationDashboard',
        meta: {
          title: 'Organization Dashboard',
          gaTrack: true,
        },
        path: '/organizations/:slug/dashboard',
        component: OrganizationDashboardPage,
      },
      {
        name: 'OrganizationUsers',
        meta: {
          title: 'Organization Members',
          gaTrack: true,
        },
        path: '/organizations/:slug/users',
        component: OrganizationUsersPage,
      },
    ],
  })

  /** Attempted to implement Vue Router's scrollBehavior function to address
   * a few use cases where scroll positioning was present, couldn't be used.
   * Result was the function scrolled to the top of page even when returning
   * falsy/empty value.
   *
   * -VueRouter 3.0.6
   */

  // auto scroll, focus, and track pageviews in GA
  router.afterEach((to, from) => {
    // if not coming from an SPA route OR initial SPA page load (no name, not in routes)
    // do not do anything... do not want to double track in GA as
    // it was already tracked in initial blade load
    if (!from.name) {
      return
    }

    if (to.meta) {
      spaMetaUpdateAndTrack(to.meta)
    }
  })

  return router
}

export default createSpaRoutes
